import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LoginComponent from "@/components/auth/LoginComponent";
import LoadingScreen from "@/components/LoadingScreen";
import { AnimateMotion } from "@/components/AnimateMotion";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsAuthenticationEvent } from "@/analytics/types";

export default function LoginPage() {
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    const [searchParams] = useSearchParams();
    const {
        completeLoginWithEmailLink,
        completeLoginWithEmailLinkAndName,
        user,
        loading: authLoading,
        setLoading,
        pendingAuthInfo
    } = useAuth();
    const [emailLinkProcessing, setEmailLinkProcessing] = useState(false);
    const [emailLinkError, setEmailLinkError] = useState<string | null>(null);
    const [emailLinkSuccess, setEmailLinkSuccess] = useState(false);
    const [emailLinkAttempted, setEmailLinkAttempted] = useState(false);
    const [nameInput, setNameInput] = useState("");
    const { track } = useAnalytics();

    // Check if this is an email link sign-in
    const isEmailLink = searchParams.get("mode") === "signIn" && searchParams.get("oobCode");

    const handleEmailLinkSignIn = useCallback(async () => {
        setEmailLinkAttempted(true); // Mark as attempted immediately to prevent re-runs
        setLoading(true);
        setEmailLinkProcessing(true);
        setEmailLinkError(null);

        try {
            const result = await completeLoginWithEmailLink();

            if (result) {
                // Check if user needs to complete setup (missing displayName)
                if (!result.displayName) {
                    // User needs to provide name - stay on login page, don't redirect
                    // The name form will be shown based on pendingAuthInfo state
                } else {
                    // User is fully set up - show success and redirect
                    setEmailLinkSuccess(true);
                    track({
                        event: AnalyticsAuthenticationEvent.USER_SIGNED_IN,
                        data: {
                            email: result.email,
                            method: result.providerData[0]?.providerId,
                            name: result.displayName
                        }
                    });
                    setTimeout(() => {
                        navigateRef.current("/");
                    }, 1000);
                }
            } else {
                setEmailLinkError("Failed to complete email link sign-in. Please try again.");
            }
        } catch (error) {
            console.error("Email link sign-in error:", error);
            setEmailLinkError("An error occurred during email link sign-in. Please try again.");
        } finally {
            setEmailLinkProcessing(false);
            setLoading(false);
        }
    }, [completeLoginWithEmailLink, setLoading, track]);

    useEffect(() => {
        // If this is an email link, process it first (but only once)
        // Skip if we already have a fully authenticated user (name setup already completed)
        if (isEmailLink && user && user.displayName) {
            // User is fully authenticated, redirect to home
            track({
                event: AnalyticsAuthenticationEvent.USER_SIGNED_IN,
                data: {
                    email: user.email,
                    method: user.providerData[0]?.providerId,
                    name: user.displayName
                }
            });
            navigateRef.current("/");
            return;
        }

        if (
            isEmailLink &&
            !emailLinkProcessing &&
            !emailLinkAttempted &&
            !(user && user.displayName)
        ) {
            handleEmailLinkSignIn();
            return;
        }

        // Never redirect during email link flow - let the flow handle its own navigation
        if (isEmailLink) {
            return;
        }

        // Only redirect for normal login flow (not email link) when user is authenticated and no pending auth info
        if (user && !authLoading && !pendingAuthInfo) {
            navigateRef.current("/");
            return;
        }
    }, [
        user,
        authLoading,
        pendingAuthInfo,
        isEmailLink,
        emailLinkAttempted,
        emailLinkProcessing,
        track,
        handleEmailLinkSignIn
    ]);

    const handleNameSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            if (!nameInput.trim()) return;

            setLoading(true);
            setEmailLinkProcessing(true);
            setEmailLinkError(null);

            try {
                const result = await completeLoginWithEmailLinkAndName(nameInput.trim());

                if (result) {
                    setEmailLinkSuccess(true);
                    track({
                        event: AnalyticsAuthenticationEvent.USER_SIGNED_IN,
                        data: {
                            email: result.email,
                            method: result.providerData[0]?.providerId,
                            name: result.displayName
                        }
                    });
                    // Small delay to show success state before redirecting
                    setTimeout(() => {
                        navigateRef.current("/");
                    }, 1000);
                } else {
                    setEmailLinkError("Failed to complete sign-in setup. Please try again.");
                }
            } catch (error) {
                console.error("Name setup error:", error);
                setEmailLinkError("An error occurred during account setup. Please try again.");
            } finally {
                setEmailLinkProcessing(false);
                setLoading(false);
            }
        },
        [completeLoginWithEmailLinkAndName, nameInput, setLoading, track]
    );

    const handleRegularLoginSuccess = useCallback(() => {
        navigateRef.current("/");
    }, []);

    // Show loading state while processing email link (but not if we're showing success)
    if (isEmailLink && (emailLinkProcessing || authLoading) && !emailLinkSuccess) {
        return <LoadingScreen />;
    }

    // Show name input form if user needs to provide their name
    if (user && pendingAuthInfo) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4">
                <img src="/images/logo.png" alt="Rapid Logo" className="mb-5 h-auto w-48" />
                <div className="max-w-md text-center">
                    <h2 className="mb-2 font-medium">Complete Your Account Setup</h2>
                    <p className="mb-6 text-sm text-tertiary">
                        Please enter your name to complete your account setup.
                    </p>
                    <form onSubmit={handleNameSubmit} className="space-y-4">
                        <div className="inputWithLabel">
                            <label htmlFor="name-setup">Full Name</label>
                            <input
                                id="name-setup"
                                type="text"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                required
                                placeholder="Enter your full name"
                                className="form-input"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={!nameInput.trim() || emailLinkProcessing}
                            className="w-full rounded-lg bg-purboflow-500 px-4 py-2 text-white transition-colors hover:bg-purboflow-600 disabled:opacity-50"
                        >
                            {emailLinkProcessing ? "Setting up..." : "Complete Setup"}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // Show success state
    if (emailLinkSuccess) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4">
                <img src="/images/logo.png" alt="Rapid Logo" className="mb-5 h-auto w-48" />
                <div className="text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-green-500/50 bg-green-500/20">
                        <i className="fas fa-check text-2xl text-green-400"></i>
                    </div>
                    <h2 className="mb-2 text-lg font-medium">Sign In Successful!</h2>
                    <p className="animate-pulse font-mono text-sm text-tertiary">
                        Redirecting you...
                    </p>
                </div>
            </div>
        );
    }

    // Show error state for email link
    if (emailLinkError) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4">
                <img src="/images/logo.png" alt="Rapid Logo" className="mb-5 h-auto w-48" />
                <div className="max-w-md text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-red-500/50 bg-red-500/20">
                        <i className="fas fa-times text-2xl text-red-400"></i>
                    </div>
                    <h2 className="mb-2 text-lg font-medium text-white">Sign In Failed</h2>
                    <p className="mb-4 text-sm text-red-400">{emailLinkError}</p>
                    <button
                        onClick={() => {
                            setEmailLinkError(null);
                            // Clear URL parameters to show regular login
                            window.history.replaceState({}, document.title, "/login");
                        }}
                        className="text-brand transition duration-200 hover:text-brand-hover"
                    >
                        Try a different sign-in method
                    </button>
                </div>
            </div>
        );
    }

    // Regular login form
    return (
        <AnimateMotion>
            <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4">
                {/* Login form */}
                <div className="rounded-lg border border-gray-300 p-8">
                    <LoginComponent
                        onSuccess={handleRegularLoginSuccess}
                        title="Create Account"
                        onClose={() => {
                            navigate("/");
                        }}
                    />
                </div>
            </div>
        </AnimateMotion>
    );
}
